<template>
  <div data-test-id="chat_request_promo_modal" class="modal-dialog modal-dialog-full h-full w-full">
    <div class="modal-content h-full">
      <div ref="modalContent" class="h-full p-4 bg-primary-600 overflow-y-auto">
        <i class="icon icon-close text-dark-200" @click="emit('close')"></i>

        <template v-if="self.profile && self.myProfile">
          <div class="relative mt-4">
            <div class="flex justify-center">
              <div class="w-fit h-fit rounded-full overflow-clip">
                <RetinaPhoto
                  :width="108"
                  :height="108"
                  :photo-id="self.myProfile.photos[0]?.id ?? ''"
                />
              </div>
              <div class="w-fit h-fit rounded-full overflow-clip">
                <RetinaPhoto
                  :width="108"
                  :height="108"
                  :photo-id="self.profile.photos[0]?.id ?? ''"
                />
              </div>
            </div>
            <div
              class="flex justify-center items-center w-16 h-16 rounded-full bg-powerlike absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 shadow-chatrequest"
            >
              <FontIcon class="icon-instant-chat_solid !text-[32px] text-white" />
            </div>
          </div>

          <div class="text-headline5 text-dark font-semibold mt-6">{{ t('title') }}</div>
          <div class="text-body2 text-dark-100 font-normal mt-4">
            {{ t('description') }}
          </div>
          <div class="text-headline7 text-dark font-medium mt-5">{{ t('chooseMessage') }}</div>
          <div class="mt-2 flex gap-2 overflow-x-auto">
            <div
              v-for="ice in self.storeChatIcebreakers.icebreakerList"
              :key="ice"
              class="text-tag text-dark font-medium py-3 px-4 bg-primary-500 rounded-full w-fit rounded-br-none whitespace-nowrap"
              @click="self.sendIceBreaker(ice)"
            >
              {{ ice }}
            </div>
          </div>
          <div class="text-headline7 text-dark font-medium mt-5">{{ t('writeNice') }}</div>
          <textarea
            v-model="self.messageText"
            :placeholder="t('tapToWrite')"
            class="mt-2 h-20 border border-dark-400 rounded p-3 w-full"
          ></textarea>
          <PrimaryBlockButton
            :is-disabled="!self.messageText.length"
            class="mt-5"
            @click="self.sendMessageText()"
          >
            <span class="flex gap-1 justify-center items-center">
              <FontIcon class="icon-instant-chat_solid !text-[18px] text-white" />
              {{ t('send') }}
            </span>
          </PrimaryBlockButton>

          <label class="flex items-center gap-2 mt-5">
            <input
              v-model="self.isSmartMatchDisabled"
              type="checkbox"
              class="h-5 w-5 checked:bg-primary"
            />
            <span class="text-caption text-dark font-normal">{{ t('disableSmartMessages') }}</span>
          </label>

          <div
            class="text-strongcaption text-dark font-medium flex items-center gap-1"
            @click="self.toggleIsRulesExpanded()"
          >
            <span class="underline cursor-pointer">{{ t('howItWorks') }}</span>
            <FontIcon v-if="self.isRulesExpanded" class="icon-chevron_top !text-[16px]" />
            <FontIcon v-else class="icon-chevron_down !text-[16px]" />
          </div>
          <div v-if="self.isRulesExpanded">
            <ol class="text-caption font-normal list-decimal pl-4 mt-3">
              <li>{{ t('step1') }}</li>
              <li>{{ t('step2') }}</li>
              <li>{{ t('step3') }}</li>
              <li>{{ t('step4') }}</li>
            </ol>
          </div>
        </template>
        <ModalSpinner v-else />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';

import { chatRequestPromoModal } from '@/components/chat-request-promo-modal/helpers/chat-request-promo-modal/chat-request-promo-modal';
import PrimaryBlockButton from '@/components/inputs/button/PrimaryBlockButton.vue';
import FontIcon from '@/components/inputs/icon/font-icon/FontIcon.vue';
import ModalSpinner from '@/components/modal/ModalSpinner.vue';
import RetinaPhoto from '@/components/retina-image/RetinaPhoto.vue';
import { useI18n } from '@/services/i18n';
import { removeNotificationHandler } from '@/services/websocket';

import { useSelf } from './hooks/use-self/use-self';
import { messages } from './i18n';

const props = defineProps<{
  userId: string;
}>();

const emit = defineEmits(['close']);

const { t } = useI18n({ messages });

const self = useSelf({
  userId: props.userId,
});

onMounted(() => {
  self.mount();
});

onUnmounted(() => {
  removeNotificationHandler(chatRequestPromoModal.handleWebsocket);
  self.destroy();
});

self.init();
</script>

<style scoped>
.black_gradient {
  background: linear-gradient(180deg, rgba(1, 33, 45, 0) 0%, #00202c 100%);
}
</style>
